/**
 * TrustReportEngagementCard — Command Centre tile.
 * Feb 2026
 *
 * Shows how school + investor leads are engaging with the auto-attached
 * Trust Report PDF: 24h/7d/30d deliveries, opens, unique opens, clicks,
 * avg time-to-first-open, and a live list of the last 15 opens (email
 * masked for privacy — never exposes raw prospect addresses).
 *
 * Auto-refreshes every 45s so a fresh open surfaces in the room without
 * a page reload — the "warm-lead signal" superpower.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Eye, MousePointerClick, TrendingUp, GraduationCap, Sparkles, ExternalLink, RefreshCw, Send, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const WINDOW_LABELS = { '24h': 'Last 24h', '7d': 'Last 7d', '30d': 'Last 30d' };

function timeAgo(iso) {
  if (!iso) return '—';
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (_) { return '—'; }
}

function minutesLabel(m) {
  if (m == null) return '—';
  if (m < 1) return '<1m';
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

const TrustReportEngagementCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [activeWindow, setActiveWindow] = useState('7d');
  // Which message_id is currently having its follow-up template fetched?
  const [draftingMsgId, setDraftingMsgId] = useState(null);
  const [draftError, setDraftError] = useState(null);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const d = await apiClient.request('/api/admin/trust-report/engagement?limit=15');
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(e?.message || 'Failed to load engagement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 45_000);
    return () => clearInterval(t);
  }, [fetchData]);

  // One-click "Draft follow-up" — fetch the lead-type-specific template
  // then hand off to the OS mail client via mailto:.
  const draftFollowUp = useCallback(async (messageId) => {
    if (!messageId || draftingMsgId) return;
    setDraftingMsgId(messageId);
    setDraftError(null);
    try {
      const tmpl = await apiClient.request(`/api/admin/trust-report/follow-up/${encodeURIComponent(messageId)}`);
      if (!tmpl?.to) throw new Error('No recipient address on record');
      const params = new URLSearchParams({
        subject: tmpl.subject || 'HiBFF follow-up',
        body: tmpl.body || '',
      });
      // Space encoding — mailto: doesn't accept `+` for spaces; force %20.
      const query = params.toString().replace(/\+/g, '%20');
      const mailto = `mailto:${encodeURIComponent(tmpl.to)}?${query}`;
      window.location.href = mailto;
    } catch (e) {
      const msg = e?.message || 'Could not draft follow-up.';
      setDraftError(
        msg.toLowerCase().includes('no send record')
          ? 'No send record for this open — likely a legacy send from before this feature shipped.'
          : msg
      );
    } finally {
      setDraftingMsgId(null);
    }
  }, [draftingMsgId]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid="trust-report-engagement-card">
        <p className="text-xs text-gray-400">Loading Trust Report engagement…</p>
      </div>
    );
  }

  const w = (data?.windows || {})[activeWindow] || { deliveries: 0, opens: 0, unique_opens: 0, clicks: 0, per_type: {} };
  const openRate = w.deliveries > 0 ? Math.round((w.opens / w.deliveries) * 100) : 0;
  const clickRate = w.deliveries > 0 ? Math.round((w.clicks / w.deliveries) * 100) : 0;
  const schoolShare = w.per_type?.school || 0;
  const investorShare = w.per_type?.investor || 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="trust-report-engagement-card">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-white to-fuchsia-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-sm">
            <FileText size={18} />
          </div>
          <div>
            <h3 className="text-sm font-black text-gray-900">Trust Report engagement</h3>
            <p className="text-[11px] text-gray-500 leading-tight">
              Opens & clicks on the auto-attached PDF from school + investor confirmation emails.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-gray-200 bg-white p-1">
            {['24h', '7d', '30d'].map(k => (
              <button
                key={k}
                onClick={() => setActiveWindow(k)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition ${
                  activeWindow === k ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`trust-eng-window-${k}`}
              >
                {k}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={fetchData}
            disabled={refreshing}
            aria-label="Refresh engagement"
            className="p-2 rounded-full text-gray-500 hover:bg-white hover:text-indigo-700 transition disabled:opacity-50"
            data-testid="trust-eng-refresh"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {err && (
        <div className="px-6 py-3 bg-red-50 text-red-700 text-xs" data-testid="trust-eng-error">{err}</div>
      )}

      {/* Stats grid */}
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3" data-testid="trust-eng-stats">
        <StatCell
          icon={FileText}
          label="Delivered"
          value={w.deliveries}
          sub={WINDOW_LABELS[activeWindow]}
          tid="trust-eng-delivered"
        />
        <StatCell
          icon={Eye}
          label="Opens"
          value={w.opens}
          sub={`${openRate}% open rate`}
          accent="emerald"
          tid="trust-eng-opens"
        />
        <StatCell
          icon={Sparkles}
          label="Unique opens"
          value={w.unique_opens}
          sub={`${w.deliveries ? Math.round((w.unique_opens / w.deliveries) * 100) : 0}% unique`}
          accent="violet"
          tid="trust-eng-unique-opens"
        />
        <StatCell
          icon={MousePointerClick}
          label="Clicks"
          value={w.clicks}
          sub={`${clickRate}% CTR`}
          accent="amber"
          tid="trust-eng-clicks"
        />
      </div>

      {/* Composition + timing */}
      <div className="px-6 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-xl px-4 py-3" data-testid="trust-eng-schools">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">
            <GraduationCap size={12} /> Schools
          </div>
          <p className="text-lg font-black text-gray-900 tabular-nums">{schoolShare}</p>
          <p className="text-[10px] text-gray-500">opens in window</p>
        </div>
        <div className="bg-gray-50 rounded-xl px-4 py-3" data-testid="trust-eng-investors">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">
            <TrendingUp size={12} /> Investors
          </div>
          <p className="text-lg font-black text-gray-900 tabular-nums">{investorShare}</p>
          <p className="text-[10px] text-gray-500">opens in window</p>
        </div>
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3" data-testid="trust-eng-first-open">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-indigo-700 mb-1">
            <Eye size={12} /> Avg. time to first open
          </div>
          <p className="text-lg font-black text-indigo-900 tabular-nums">{minutesLabel(data?.avg_first_open_minutes)}</p>
          <p className="text-[10px] text-indigo-700">
            {data?.sampled_open_count_30d ? `${data.sampled_open_count_30d} samples · last 30d` : 'awaiting first pair'}
          </p>
        </div>
      </div>

      {/* Recent opens timeline */}
      <div className="border-t border-gray-100 px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Recent opens</p>
          <Link
            to="/status/history"
            className="text-[11px] font-semibold text-indigo-700 hover:underline inline-flex items-center gap-1"
            data-testid="trust-eng-archive-link"
          >
            Audit archive <ExternalLink size={11} />
          </Link>
        </div>
        {(!data?.recent_opens || data.recent_opens.length === 0) ? (
          <p className="text-xs text-gray-400 italic py-3" data-testid="trust-eng-empty">
            No opens recorded yet. Once a school or investor cracks their Trust Report, it lands here in real time.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto" data-testid="trust-eng-recent-list">
            {data.recent_opens.map((r, i) => {
              const isSchool = r.lead_type === 'school';
              const isDrafting = draftingMsgId === r.message_id;
              return (
                <li
                  key={`${r.masked_email}-${i}`}
                  className="py-2 flex items-center justify-between gap-2"
                  data-testid={`trust-eng-open-${i}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSchool ? 'bg-emerald-100 text-emerald-700' : 'bg-fuchsia-100 text-fuchsia-700'
                      }`}
                    >
                      {isSchool ? <GraduationCap size={12} /> : <TrendingUp size={12} />}
                    </span>
                    <div className="min-w-0">
                      {(r.org_name || r.recipient_first_name) && (
                        <p className="text-xs font-bold text-gray-900 truncate" data-testid={`trust-eng-attribution-${i}`}>
                          {r.org_name || 'Unknown org'}
                          {r.recipient_first_name && (
                            <span className="text-gray-500 font-normal"> — {r.recipient_first_name}</span>
                          )}
                        </p>
                      )}
                      <p className="text-[11px] text-gray-500 truncate font-mono">{r.masked_email}</p>
                      <p className="text-[10px] text-gray-500">
                        {isSchool ? 'School applicant' : 'Investor lead'}
                        {r.minutes_since_send != null && <> · opened <strong>{minutesLabel(r.minutes_since_send)}</strong> after send</>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.message_id && (
                      <button
                        type="button"
                        onClick={() => draftFollowUp(r.message_id)}
                        disabled={isDrafting}
                        aria-label="Draft warm follow-up reply"
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border border-indigo-200 bg-white text-indigo-700 hover:border-indigo-500 hover:bg-indigo-50 transition disabled:opacity-60"
                        data-testid={`trust-eng-draft-followup-${i}`}
                      >
                        {isDrafting ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                        {isDrafting ? 'Drafting…' : 'Draft follow-up'}
                      </button>
                    )}
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{timeAgo(r.opened_at)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {draftError && (
          <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="trust-eng-draft-error">
            {draftError}
          </p>
        )}
      </div>

      <div className="px-6 py-2.5 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-500 flex items-center justify-between gap-2 flex-wrap">
        <span>Auto-refreshes every 45s · emails masked for privacy · powered by Postmark webhooks.</span>
        {data?.generated_at && <span>Snapshot {timeAgo(data.generated_at)}</span>}
      </div>
    </div>
  );
};

const StatCell = ({ icon: Icon, label, value, sub, accent, tid }) => {
  const palette = accent === 'emerald'
    ? { bg: '#ECFDF5', color: '#059669' }
    : accent === 'violet'
      ? { bg: '#F5F3FF', color: '#6D28D9' }
      : accent === 'amber'
        ? { bg: '#FFFBEB', color: '#B45309' }
        : { bg: '#F9FAFB', color: '#1F2937' };
  return (
    <div className="rounded-xl border border-gray-100 px-4 py-3" style={{ backgroundColor: palette.bg }} data-testid={tid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} style={{ color: palette.color }} />
        <span className="text-[10px] uppercase font-bold tracking-wider" style={{ color: palette.color }}>{label}</span>
      </div>
      <p className="text-2xl font-black tabular-nums" style={{ color: palette.color }}>{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

export default TrustReportEngagementCard;
